// Component: NotificationDropdown
// Purpose: Dropdown panel listing notification items with relative timestamps.
//          Supports per-item mark-read and bulk mark-all-read.
//          Unread items are visually distinguished (bold title + accent border).
//          #118 WP10 (DONE-WHEN G, R5.2): renders a severity/category badge plus human
//          strategy(sii)/account context instead of dropping those returned fields.
//          Tap navigation to `detail.action.route` is explicitly OUT of #118 scope (owned
//          by #120, per Refine R5.2 AC / design B3) — this component never navigates.

import React, { useCallback } from 'react';
import { markRead, markAllRead } from '../../utils/notificationApi';

// --> Helpers

//! #118 WP10 — severity -> badge color, closed switch (no fallback color hides an unknown
//!  severity silently; falls back to a neutral gray so an unmapped severity is still visible,
//!  never invisible/crashing).
const SEVERITY_BADGE_CLASS = {
  USER_ACTION_REQUIRED: 'bg-amber-500/20 text-amber-600 dark:text-amber-400',
  USER_INFO: 'bg-blue-500/20 text-blue-600 dark:text-blue-400',
  OPERATIONS: 'bg-purple-500/20 text-purple-600 dark:text-purple-400',
  COMPLIANCE: 'bg-red-500/20 text-red-600 dark:text-red-400',
};

function severityBadgeClass(severity) {
  return SEVERITY_BADGE_CLASS[severity] || 'bg-gray-500/20 text-gray-500 dark:text-gray-400';
}

/**
 * Human strategy(sii)/account context line — sii is
 * {user_id}:{strategy_id}:{version_id}:{instrument_id}:{timeframe}; only the strategy_id and
 * instrument_id components are shown (never the raw sii string on a human surface).
 */
function contextLine(notification) {
  const parts = [];
  if (notification.sii) {
    const siiParts = String(notification.sii).split(':');
    const strategyId = siiParts.length >= 2 ? siiParts[1] : null;
    const instrumentId = siiParts.length >= 4 ? siiParts[3] : null;
    if (strategyId) parts.push(`Strategy ${strategyId}`);
    if (instrumentId) parts.push(instrumentId);
  }
  if (notification.account_id) {
    parts.push(`Account ${String(notification.account_id).slice(0, 8)}`);
  }
  return parts.join(' · ');
}

/**
 * Return a human-readable relative timestamp string for a given ISO date string.
 * Uses IST (Asia/Kolkata) locale for display per CLAUDE.md §Timezones.
 */
function relativeTime(isoString) {
  if (!isoString) return '';
  const then = new Date(isoString);
  const nowMs = Date.now();
  const diffSec = Math.floor((nowMs - then.getTime()) / 1000);

  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  // Fall back to locale date string in IST for older items
  return then.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'short', timeStyle: 'short' });
}

// --> Component

/**
 * Props:
 *   items        {Array}    - Notification objects from the API.
 *   onMarkRead   {Function} - Called with (id) after a successful per-item mark-read.
 *   onMarkAll    {Function} - Called with no args after a successful mark-all-read.
 *   loading      {boolean}  - Whether items are still loading.
 *   error        {string|null} - Error message to display, or null.
 */
const NotificationDropdown = ({ items = [], onMarkRead, onMarkAll, loading = false, error = null }) => {

  // --> Handlers

  const handleItemClick = useCallback(async (notification) => {
    //! Guard: only call API for unread items
    if (notification.read_at !== null && notification.read_at !== undefined) return;
    try {
      await markRead(notification.id);
      if (onMarkRead) onMarkRead(notification.id);
    } catch (err) {
      console.log('[DIAG] NotificationDropdown markRead error', { id: notification.id, err: err.message });
    }
  }, [onMarkRead]);

  const handleMarkAll = useCallback(async () => {
    try {
      await markAllRead();
      if (onMarkAll) onMarkAll();
    } catch (err) {
      console.log('[DIAG] NotificationDropdown markAllRead error', { err: err.message });
    }
  }, [onMarkAll]);

  // --> Render

  return (
    <div
      className="absolute right-0 mt-2 w-80 rounded-lg shadow-xl bg-bg-2 border border-[var(--divider)] z-50 flex flex-col"
      style={{ maxHeight: '420px' }}
    >
      {/* Header row */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--divider)]">
        <span className="text-sm font-semibold text-fg-2">Notifications</span>
        <button
          onClick={handleMarkAll}
          className="text-xs text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
          disabled={loading}
        >
          Mark all read
        </button>
      </div>

      {/* Body — scrollable list */}
      <div className="overflow-y-auto flex-1">
        {loading && (
          <div className="px-4 py-6 text-center text-sm text-fg-3">
            Loading...
          </div>
        )}

        {!loading && error && (
          <div className="px-4 py-6 text-center text-sm text-red-500">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-fg-3">
            No notifications
          </div>
        )}

        {!loading && !error && items.map((notification) => {
          const isUnread = notification.read_at === null || notification.read_at === undefined;
          return (
            <div
              key={notification.id}
              onClick={() => handleItemClick(notification)}
              className={`
                px-4 py-3 cursor-pointer border-b border-[var(--divider)] last:border-b-0
                hover:bg-bg-3 transition-colors
                ${isUnread ? 'border-l-2 border-l-blue-500' : ''}
              `}
            >
              <div className="flex items-center gap-1.5 flex-wrap">
                <div className={`text-sm ${isUnread ? 'font-semibold text-fg-1' : 'font-normal text-fg-2'}`}>
                  {notification.title}
                </div>
                {notification.severity && (
                  <span
                    className={`text-[10px] leading-none px-1.5 py-0.5 rounded-full font-medium ${severityBadgeClass(notification.severity)}`}
                  >
                    {notification.severity}
                  </span>
                )}
                {notification.category && (
                  <span className="text-[10px] leading-none px-1.5 py-0.5 rounded-full bg-bg-3 text-fg-3 font-medium">
                    {notification.category}
                  </span>
                )}
              </div>
              {notification.body && (
                <div className="text-xs text-fg-2 mt-0.5 line-clamp-2">
                  {notification.body}
                </div>
              )}
              {contextLine(notification) && (
                <div className="text-xs text-fg-3 mt-0.5">
                  {contextLine(notification)}
                </div>
              )}
              <div className="text-xs text-fg-3 mt-1">
                {relativeTime(notification.created_at)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NotificationDropdown;
